import { Archive, ArchiveRestore, ArrowLeft, CheckCircle2, CircleAlert, Copy, Download, Eye, FileText, MoreHorizontal, Plus, RefreshCw, Save, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { ApiError, apiBlob, apiRequest } from "../../lib/api";
import { formatCalendarDate, formatTimestamp } from "../../lib/format";
import { uploadTripDocument, useAdminRegions, useAdminTrip, useAdminTrips, useInvalidateAll } from "../../lib/queries";
import { amountWithCurrency } from "../../lib/reservations";
import type { AdminTrip, TripContent } from "../../types";
import { AdminPhotoField, ConfirmButton, Feedback, Field, KeyValues, Pill, useAction } from "./shared";
import { DeparturesEditor, GalleryField, PricingEditor, PricingHistoryPanel } from "./AdminTripPricing";
import { AdminTripSavers } from "./AdminTripSavers";
import { AccommodationsEditor, BasicsEditor, DuplicatesBanner, InclusionsEditor, ItineraryEditor, NewTripDialog, TRIP_STATUS, TripDangerZone } from "./AdminTripEditors";

/**
 * Trip management (Flow E). The list: search, status filters, likely duplicates and per-row
 * actions. The editor: one sticky action bar (save, preview, status) over tabs — Resumen,
 * Itinerario, Tarifas y salidas, Incluye y notas, Publicación — each a structured form over the
 * same universal JSON, which stays reachable as an advanced view.
 */

type Filter = "all" | "published" | "draft" | "archived" | "duplicates";
const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "all", label: "Todos" }, { id: "published", label: "Publicados" }, { id: "draft", label: "Borradores" }, { id: "archived", label: "Archivados" }, { id: "duplicates", label: "Posibles duplicados" }
];
const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Explora's category chips (the same ids as the destination cards). */
const TRIP_CATEGORY_OPTIONS = [
  { id: "playa", label: "Playa" },
  { id: "ciudad", label: "Ciudad" },
  { id: "aventura", label: "Aventura" },
  { id: "cultura", label: "Cultura" }
] as const;

export function AdminTripsPage() {
  usePageHeader("Viajes", "Crea viajes desde un Word o a mano, revísalos por secciones y publica la tarjeta y la página.");
  const index = useAdminTrips();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [creating, setCreating] = useState(false);

  const trips = index.data?.trips ?? [];
  const counts = index.data?.counts ?? { all: 0, published: 0, draft: 0, archived: 0, duplicates: 0 };
  const visible = useMemo(() => {
    const needle = normalize(query.trim());
    return trips.filter((trip) => (filter === "all" || (filter === "duplicates" ? (trip.duplicates?.length ?? 0) > 0 : trip.status === filter))
      && (!needle || [trip.name, trip.slug, trip.routeDisplay, trip.region?.name ?? "", ...trip.countries, ...trip.cities].some((value) => normalize(value).includes(needle))));
  }, [trips, filter, query]);

  const rowAction = (trip: AdminTrip, key: string, call: () => Promise<string>) => action.run(`${key}-${trip.id}`, async () => { const notice = await call(); await invalidate(); return notice; });

  if (index.isLoading) return <div className="page-state"><Spinner label="Cargando viajes" /></div>;

  return (
    <div className="page-container admin-stack">
      <div className="discover-toolbar">
        <label className="search-field"><Search size={18} aria-hidden="true" /><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nombre, ruta, país o dirección" aria-label="Buscar viajes" /></label>
        <button type="button" className="primary-button solid" onClick={() => setCreating(true)}><Plus size={16} aria-hidden="true" /> Nuevo viaje</button>
      </div>
      <div className="chip-row" role="group" aria-label="Filtrar por estado">
        {FILTERS.map((option) => {
          const count = option.id === "all" ? counts.all : option.id === "duplicates" ? counts.duplicates : counts[option.id];
          if (option.id === "duplicates" && count === 0) return null;
          return <button key={option.id} type="button" className={`chip${filter === option.id ? " active" : ""}${option.id === "duplicates" ? " warn" : ""}`} aria-pressed={filter === option.id} onClick={() => setFilter(option.id)}>{option.label} <span className="chip-count" translate="no">{count}</span></button>;
        })}
      </div>
      <Feedback error={action.error} notice={action.notice} />

      {trips.length === 0 ? (
        <div className="empty-state tall">
          <FileText size={28} aria-hidden="true" />
          <strong>Aún no hay viajes</strong>
          <span>Sube el itinerario en Word o crea el primero a mano.</span>
          <button type="button" className="primary-button solid" onClick={() => setCreating(true)}><Plus size={16} aria-hidden="true" /> Nuevo viaje</button>
        </div>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table className="admin-table trips-table">
            <thead><tr><th>Viaje</th><th>Estado</th><th>Desde</th><th>Salidas</th><th>Tarifas</th><th>Uso</th><th>Actualizado</th><th /></tr></thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={8} className="table-empty">Sin viajes con ese filtro o búsqueda.</td></tr>}
              {visible.map((trip) => {
                const duplicates = trip.duplicates ?? [];
                const usage = trip.usage ?? { requests: 0, apartados: 0, saved: 0 };
                const busy = action.busy?.endsWith(trip.id) ?? false;
                return (
                  <tr key={trip.id}>
                    <td>
                      <Link to={`/admin/viajes/${trip.id}`} className="trip-row-name"><strong>{trip.name || "(sin nombre)"}</strong></Link>
                      <span className="request-meta">/{trip.slug}{trip.region ? ` · ${trip.region.name}` : ""}{trip.durationDays ? ` · ${trip.durationDays} días` : ""}</span>
                      {duplicates.length > 0 && <span className="pill warn" style={{ marginTop: 4 }} title={duplicates.map((item) => item.name).join(", ")}><Copy size={11} aria-hidden="true" /> Se parece a {duplicates.length === 1 ? `«${duplicates[0]!.name}»` : `${duplicates.length} viajes`}</span>}
                    </td>
                    <td>
                      <Pill label={TRIP_STATUS[trip.status].label} tone={TRIP_STATUS[trip.status].tone} />
                      <span className="request-meta" translate="no">v{trip.currentVersion}{trip.publishedVersion ? ` · pub. v${trip.publishedVersion}` : ""}</span>
                      {trip.status !== "archived" && trip.publicationBlockers.length > 0 && <span className="request-meta warn-text" translate="no">{trip.publicationBlockers.length} pendientes</span>}
                    </td>
                    <td translate="no">{trip.priceFrom ? amountWithCurrency(trip.priceFrom.amount, trip.priceFrom.currency) : "—"}</td>
                    <td translate="no">{trip.departureCount}{trip.nextDeparture ? <><br /><span className="request-meta">próx. {formatCalendarDate(trip.nextDeparture)}</span></> : null}</td>
                    <td translate="no">
                      {trip.pricingHistory && trip.pricingHistory.entries > 0
                        ? <>{trip.pricingHistory.changes} {trip.pricingHistory.changes === 1 ? "cambio" : "cambios"}<br /><span className="request-meta">{trip.pricingHistory.lastChangeAt ? `último ${formatCalendarDate(trip.pricingHistory.lastChangeAt.slice(0, 10))}` : trip.pricingHistory.currentSince ? `vigente desde ${formatCalendarDate(trip.pricingHistory.currentSince.slice(0, 10))}` : ""}</span></>
                        : <span className="request-meta">Sin historial</span>}
                    </td>
                    <td translate="no">{usage.requests + usage.apartados + usage.saved === 0 ? <span className="request-meta">—</span> : <>{usage.requests} sol. · {usage.apartados} apart.<br /><span className="request-meta">{usage.saved} guardados</span></>}</td>
                    <td translate="no">{formatTimestamp(trip.updatedAt)}</td>
                    <td>
                      <div className="row-actions">
                        <Link className="secondary-button small" to={`/admin/viajes/${trip.id}`}>Abrir</Link>
                        <details className="row-menu">
                          <summary className="icon-button ghost" aria-label={`Más acciones para ${trip.name}`}>{busy ? <Spinner /> : <MoreHorizontal size={16} aria-hidden="true" />}</summary>
                          <div className="row-menu-list" role="menu">
                            <a role="menuitem" href={`/viajes/${trip.slug}?preview=1`} target="_blank" rel="noreferrer"><Eye size={14} aria-hidden="true" /> Vista previa</a>
                            <button role="menuitem" type="button" onClick={() => void rowAction(trip, "dup", async () => { const payload = await apiRequest<{ data: { trip: AdminTrip } }>(`/api/v1/admin/trips/${trip.id}/duplicate`, { method: "POST", body: "{}" }); navigate(`/admin/viajes/${payload.data.trip.id}`); return `Copia creada: ${payload.data.trip.name}.`; })}><Copy size={14} aria-hidden="true" /> Duplicar</button>
                            {trip.status === "published" && <button role="menuitem" type="button" onClick={() => void rowAction(trip, "arch", async () => { await apiRequest(`/api/v1/admin/trips/${trip.id}/archive`, { method: "POST", body: "{}" }); return `«${trip.name}» archivado: ya no aparece en Explora.`; })}><Archive size={14} aria-hidden="true" /> Archivar</button>}
                            {trip.status === "archived" && <button role="menuitem" type="button" onClick={() => void rowAction(trip, "rest", async () => { await apiRequest(`/api/v1/admin/trips/${trip.id}/unarchive`, { method: "POST", body: "{}" }); return `«${trip.name}» restaurado como borrador.`; })}><ArchiveRestore size={14} aria-hidden="true" /> Restaurar</button>}
                            {trip.status !== "published" && (
                              <ConfirmButton
                                danger label="Eliminar" icon={<Trash2 size={14} aria-hidden="true" />} title="Eliminar viaje"
                                message={<p>Se elimina <strong>{trip.name}</strong> (/{trip.slug}) con sus versiones, historial de tarifas y Word. {usage.requests + usage.apartados > 0 ? <>Se desvinculan <strong>{usage.requests} solicitudes y {usage.apartados} Apartados</strong>, que conservan el nombre del viaje.</> : "Nada más hace referencia a él."}</p>}
                                confirmLabel="Eliminar definitivamente"
                                onConfirm={async () => { await apiRequest(`/api/v1/admin/trips/${trip.id}`, { method: "DELETE" }); await invalidate(); }}
                              />
                            )}
                          </div>
                        </details>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <NewTripDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

// --- Editor -----------------------------------------------------------------------------------------

type Tab = "resumen" | "itinerario" | "tarifas" | "incluye" | "publicacion" | "ahorradores";
const TABS: Array<{ id: Tab; label: string }> = [
  { id: "resumen", label: "Resumen" }, { id: "itinerario", label: "Itinerario y hoteles" }, { id: "tarifas", label: "Tarifas y salidas" }, { id: "incluye", label: "Incluye y notas" }, { id: "publicacion", label: "Publicación" },
  { id: "ahorradores", label: "Ahorradores" }
];

/** Which tab a publication blocker or review finding belongs to, so the tabs can carry a count. */
function tabFor(finding: string): Tab {
  if (/^Falta (pricing|availability)|^pricing|^availability/.test(finding)) return "tarifas";
  if (/^Falta itinerary|^itinerary|duración|hoteles/.test(finding)) return "itinerario";
  if (/^Falta included|^Falta excluded|important_notes/.test(finding)) return "incluye";
  return "resumen";
}

export function AdminTripPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detail = useAdminTrip(id);
  const invalidate = useInvalidateAll();
  const action = useAction();
  const trip = detail.data?.trip;
  usePageHeader(trip?.name || "Viaje", trip ? `/${trip.slug} · ${TRIP_STATUS[trip.status].label}` : undefined);
  const tab = (searchParams.get("tab") as Tab | null) ?? "resumen";
  const setTab = (next: Tab) => { const params = new URLSearchParams(searchParams); if (next === "resumen") params.delete("tab"); else params.set("tab", next); setSearchParams(params, { replace: true }); };

  const regions = useAdminRegions();
  const [slug, setSlug] = useState("");
  const [heroPhotoId, setHeroPhotoId] = useState<string | null>(null);
  const [galleryIds, setGalleryIds] = useState<string[]>([]);
  const [regionId, setRegionId] = useState<string | null>(null);
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [confirmReview, setConfirmReview] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!trip) return;
    setSlug(trip.slug);
    setHeroPhotoId(trip.heroPhotoId);
    setGalleryIds((trip.gallery ?? []).map((photo) => photo.id).filter((value): value is string => Boolean(value)));
    setRegionId(trip.regionId);
    setDestinationId(trip.destinationId);
    setJson(JSON.stringify(trip.content, null, 2));
    setJsonError(null);
    setDirty(false);
  }, [trip?.id, trip?.currentVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);

  const parsed = useMemo((): TripContent | null => { try { setJsonError(null); return JSON.parse(json) as TripContent; } catch (error) { setJsonError(error instanceof Error ? error.message : "JSON inválido"); return null; } }, [json]);
  const update = (next: TripContent) => { setJson(JSON.stringify(next, null, 2)); setDirty(true); };

  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando viaje" /></div>;
  if (detail.isError || !trip) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos este viaje</strong><Link className="secondary-button" to="/admin/viajes">Volver</Link></div>;
  const review = trip.validation.review;
  const findingsByTab = new Map<Tab, number>();
  for (const finding of trip.publicationBlockers) findingsByTab.set(tabFor(finding), (findingsByTab.get(tabFor(finding)) ?? 0) + 1);

  async function downloadSource() {
    if (!trip) return;
    setDownloadError(null); setDownloading(true);
    try {
      const blob = await apiBlob(`/api/v1/admin/trips/${trip.id}/source`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = trip.source?.fileName ?? "itinerario.docx";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) { setDownloadError(error instanceof ApiError ? error.message : "No pudimos descargar el archivo. Inténtalo de nuevo."); }
    finally { setDownloading(false); }
  }

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!parsed) return;
    await action.run("save", async () => {
      await apiRequest(`/api/v1/admin/trips/${trip.id}`, { method: "PATCH", body: JSON.stringify({ slug, heroPhotoId, galleryPhotoIds: galleryIds, regionId, destinationId, content: parsed, note: note || undefined }) });
      await invalidate();
      setNote(""); setDirty(false);
      return "Borrador guardado. La versión publicada no cambia hasta que publiques.";
    });
  };
  const publish = async () => {
    await action.run("publish", async () => {
      try {
        await apiRequest(`/api/v1/admin/trips/${trip.id}/publish`, { method: "POST", body: JSON.stringify({ confirmReview, note: note || undefined }) });
      } catch (cause) {
        if (cause instanceof ApiError && cause.details && typeof cause.details === "object" && "blockers" in cause.details) {
          throw new ApiError(`${cause.message} ${(cause.details as { blockers: string[] }).blockers.join(" · ")}`, cause.code);
        }
        throw cause;
      }
      await invalidate();
      return "Publicado: la tarjeta en Explora y la página del viaje muestran la misma versión.";
    });
  };
  const reupload = async (file: File) => {
    await action.run("upload", async () => { await uploadTripDocument(file, trip.id); await invalidate(); return "Nuevo Word extraído a un borrador nuevo. Revisa los hallazgos antes de publicar."; });
    if (fileInput.current) fileInput.current.value = "";
  };
  const setWeb = (key: "seo_title" | "meta_description" | "slug" | "highlights" | "categories" | "keywords", value: string | string[]) => { if (parsed) update({ ...parsed, web: { ...parsed.web, [key]: value } }); };

  return (
    <div className="page-container admin-stack trip-editor">
      <Link className="back-button" to="/admin/viajes"><ArrowLeft size={16} aria-hidden="true" /> Viajes</Link>

      <div className="editor-bar">
        <div className="editor-bar-copy">
          <Pill label={TRIP_STATUS[trip.status].label} tone={TRIP_STATUS[trip.status].tone} />
          <span className="request-meta" translate="no">Borrador v{trip.currentVersion}{trip.publishedVersion ? ` · publicada v${trip.publishedVersion}` : " · nunca publicado"}{dirty ? " · cambios sin guardar" : ""}</span>
        </div>
        <div className="inline-actions">
          <input className="standalone-input small" value={note} onChange={(event) => setNote(event.target.value)} maxLength={300} placeholder="Nota de la versión (qué cambió)" aria-label="Nota de la versión" style={{ minWidth: 220 }} />
          <a className="secondary-button small" href={`/viajes/${trip.slug}?preview=1`} target="_blank" rel="noreferrer"><Eye size={15} aria-hidden="true" /> Vista previa</a>
          <button type="button" className={`primary-button small${dirty ? " solid" : ""}`} onClick={() => void save()} disabled={!parsed || action.busy !== null}>{action.busy === "save" ? <Spinner /> : <Save size={15} aria-hidden="true" />} Guardar borrador</button>
        </div>
      </div>
      <Feedback error={action.error} notice={action.notice} />
      {jsonError && <div className="form-error" role="alert">JSON inválido: {jsonError}</div>}
      <DuplicatesBanner trip={trip} duplicates={trip.duplicates ?? []} />

      <nav className="subnav" aria-label="Secciones del viaje">
        {TABS.map((item) => {
          const count = item.id === "publicacion" ? trip.publicationBlockers.length : findingsByTab.get(item.id) ?? 0;
          return <button key={item.id} type="button" className={`subnav-item${tab === item.id ? " active" : ""}`} aria-current={tab === item.id ? "page" : undefined} onClick={() => setTab(item.id)}>{item.label}{count > 0 && <span className="subnav-count" title={`${count} pendientes para publicar`}>{count}</span>}</button>;
        })}
      </nav>

      {tab === "ahorradores" && <AdminTripSavers tripId={trip.id} />}

      {parsed && tab === "resumen" && (
        <form className="admin-stack" onSubmit={save}>
          <BasicsEditor content={parsed} onChange={update} />
          <div className="editor-block">
            <div className="section-heading" style={{ margin: 0 }}><div><span className="page-eyebrow">Explora</span><h3>Dirección, catálogo y fotos</h3></div></div>
            <div className="field-grid">
              <Field label="Identificador (URL)" htmlFor="t-slug" hint="Minúsculas, números y guiones. Cambiarlo rompe enlaces ya compartidos."><input id="t-slug" className="standalone-input" value={slug} onChange={(event) => { setSlug(event.target.value); setWeb("slug", event.target.value); }} pattern="[a-z0-9]+(-[a-z0-9]+)*" /></Field>
              <Field label="Chips destacados" htmlFor="t-high" hint="Hasta cinco, separados por coma."><input id="t-high" className="standalone-input" defaultValue={parsed.web.highlights.join(", ")} onBlur={(event) => setWeb("highlights", event.target.value.split(/\n|,/).map((item) => item.trim()).filter(Boolean).slice(0, 5))} /></Field>
            </div>
            <div className="field-grid">
              <Field label="Categorías en Explora" htmlFor="t-categories" hint="El viaje aparece al filtrar por estas categorías. Puedes marcar varias.">
                <div id="t-categories" className="chip-row" role="group" aria-label="Categorías en Explora">
                  {TRIP_CATEGORY_OPTIONS.map((option) => {
                    const chosen = parsed.web.categories ?? [];
                    const active = chosen.includes(option.id);
                    return <button key={option.id} type="button" className={`chip small${active ? " active" : ""}`} aria-pressed={active} onClick={() => setWeb("categories", active ? chosen.filter((item) => item !== option.id) : [...chosen, option.id])}>{option.label}</button>;
                  })}
                </div>
              </Field>
              <Field label="Palabras clave de búsqueda" htmlFor="t-keywords" hint="Separadas por coma; no se muestran. Ej.: Torre Eiffel, luna de miel, safari.">
                <input id="t-keywords" className="standalone-input" defaultValue={(parsed.web.keywords ?? []).join(", ")} onBlur={(event) => setWeb("keywords", event.target.value.split(/\n|,/).map((item) => item.trim()).filter(Boolean).map((item) => item.slice(0, 60)).slice(0, 30))} />
              </Field>
            </div>
            <div className="field-grid">
              <Field label="Región en Explora" htmlFor="t-region" hint="La tarjeta del viaje aparece dentro de esta región.">
                <select id="t-region" className="standalone-input" value={destinationId ? (regions.data?.find((region) => region.destinations.some((destination) => destination.id === destinationId))?.id ?? regionId ?? "") : regionId ?? ""} onChange={(event) => { setRegionId(event.target.value || null); setDestinationId(null); setDirty(true); }} disabled={Boolean(destinationId)}>
                  <option value="">Sin región</option>
                  {(regions.data ?? []).map((region) => <option key={region.id} value={region.id}>{region.name}</option>)}
                </select>
              </Field>
              <Field label="Destino vinculado" htmlFor="t-destination" hint="La tarjeta de ese destino abrirá esta página con «Ver viaje».">
                <select id="t-destination" className="standalone-input" value={destinationId ?? ""} onChange={(event) => { setDestinationId(event.target.value || null); setDirty(true); }}>
                  <option value="">Sin destino</option>
                  {(regions.data ?? []).filter((region) => !regionId || destinationId || region.id === regionId).flatMap((region) => region.destinations.map((destination) => <option key={destination.id} value={destination.id}>{region.name} · {destination.name} ({destination.country})</option>))}
                </select>
              </Field>
            </div>
            <AdminPhotoField kind="destination" value={heroPhotoId} onChange={(value) => { setHeroPhotoId(value); setDirty(true); }} label="Foto principal (hero)" />
            <GalleryField ids={galleryIds} initial={trip.gallery ?? []} onChange={(ids) => { setGalleryIds(ids); setDirty(true); }} />
            <div className="field-grid">
              <Field label="SEO · título" htmlFor="t-seo"><input id="t-seo" className="standalone-input" value={parsed.web.seo_title} onChange={(event) => setWeb("seo_title", event.target.value)} maxLength={300} /></Field>
              <Field label="SEO · descripción" htmlFor="t-meta"><input id="t-meta" className="standalone-input" value={parsed.web.meta_description} onChange={(event) => setWeb("meta_description", event.target.value)} maxLength={400} /></Field>
            </div>
          </div>
        </form>
      )}

      {parsed && tab === "itinerario" && (
        <div className="admin-stack">
          <ItineraryEditor key={`it-${trip.currentVersion}`} content={parsed} onChange={update} />
          <AccommodationsEditor content={parsed} onChange={update} />
        </div>
      )}

      {parsed && tab === "tarifas" && (
        <div className="admin-stack">
          <PricingEditor key={`pricing-${trip.currentVersion}`} content={parsed} onChange={update} />
          <DeparturesEditor key={`departures-${trip.currentVersion}`} content={parsed} onChange={update} />
          <PricingHistoryPanel tripId={trip.id} />
        </div>
      )}

      {parsed && tab === "incluye" && <div className="admin-stack"><InclusionsEditor content={parsed} onChange={update} /></div>}

      {tab === "publicacion" && (
        <div className="admin-two-col">
          <div className="admin-stack">
            <section className="card">
              <span className="rail-title">Publicación</span>
              {trip.publicationBlockers.length ? (
                <>
                  <p className="field-hint">Pendientes que impiden publicar. Cada uno abre la sección donde se resuelve:</p>
                  <ul className="form-error-list blockers">{trip.publicationBlockers.map((blocker) => <li key={blocker}><button type="button" className="link-button" onClick={() => setTab(tabFor(blocker))}>{blocker}</button></li>)}</ul>
                </>
              ) : <p className="inline-notice"><CheckCircle2 size={16} aria-hidden="true" /> Sin pendientes estructurales.</p>}
              {review && review.ambiguities.length > 0 && (
                <>
                  <p className="field-hint">Ambigüedades a confirmar (no bloquean):</p>
                  <ul className="form-error-list muted">{review.ambiguities.map((item) => <li key={item}>{item}</li>)}</ul>
                </>
              )}
              {dirty && <div className="form-message" role="status">Tienes cambios sin guardar. Guarda el borrador antes de publicar para que se incluyan.</div>}
              <label className="check-row" style={{ marginTop: 12 }}>
                <input type="checkbox" checked={confirmReview} onChange={(event) => setConfirmReview(event.target.checked)} />
                <span>Revisé el contenido contra el Word o la fuente y los campos web están completos.</span>
              </label>
              <div className="inline-actions" style={{ marginTop: 12 }}>
                <button type="button" className="primary-button solid" onClick={() => void publish()} disabled={trip.publicationBlockers.length > 0 || !confirmReview || action.busy !== null || dirty}>{action.busy === "publish" ? <Spinner /> : <CheckCircle2 size={15} aria-hidden="true" />} {trip.status === "published" ? "Publicar nueva versión" : "Publicar"}</button>
                <a className="secondary-button" href={`/viajes/${trip.slug}?preview=1`} target="_blank" rel="noreferrer"><Eye size={15} aria-hidden="true" /> Vista previa</a>
              </div>
              <KeyValues rows={[
                { label: "Versión publicada", value: trip.publishedVersion ? `v${trip.publishedVersion}` : "—" },
                { label: "Publicado", value: trip.publishedAt ? formatTimestamp(trip.publishedAt) : "—" },
                { label: "Revisado", value: trip.reviewedAt ? formatTimestamp(trip.reviewedAt) : "Pendiente" },
                { label: "Solicitudes", value: String(detail.data?.usage.requests ?? 0) },
                { label: "Apartados", value: String(detail.data?.usage.apartados ?? 0) },
                { label: "Guardado por", value: `${detail.data?.usage.saved ?? 0} clientes` }
              ]} />
            </section>

            <section className="card">
              <span className="rail-title">Origen</span>
              {trip.source ? (
                <>
                  <p className="request-meta"><FileText size={14} aria-hidden="true" /> {trip.source.fileName}{trip.source.uploadedAt ? ` · ${formatTimestamp(trip.source.uploadedAt)}` : ""}</p>
                  <div className="inline-actions">
                    <button type="button" className="secondary-button small" disabled={downloading} onClick={() => void downloadSource()}><Download size={14} aria-hidden="true" /><span>{downloading ? "Descargando…" : "Descargar Word"}</span></button>
                  </div>
                  {downloadError && <div className="form-error" role="alert">{downloadError}</div>}
                </>
              ) : <p className="field-hint">Creado a mano, sin archivo de origen.</p>}
              <label className="secondary-button small" style={{ cursor: "pointer", marginTop: 10, display: "inline-flex" }}>
                <RefreshCw size={14} aria-hidden="true" /> {action.busy === "upload" ? "Extrayendo…" : "Subir nuevo Word (nueva versión)"}
                <input ref={fileInput} type="file" accept=".docx" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void reupload(file); }} disabled={action.busy !== null} />
              </label>
              <p className="field-hint">Un nuevo Word crea un borrador nuevo; la versión publicada no cambia hasta que la apruebes y publiques.</p>
            </section>

            <details className="json-details card">
              <summary>JSON universal (Apéndice A) · edición avanzada</summary>
              <Field label="JSON universal" htmlFor="t-json" hint="Fuente de verdad del itinerario. El bloque source no se edita aquí; review se recalcula al guardar.">
                <textarea id="t-json" className="standalone-input textarea mono" rows={22} value={json} onChange={(event) => { setJson(event.target.value); setDirty(true); }} spellCheck={false} />
              </Field>
            </details>
          </div>

          <div className="admin-stack">
            <section className="card">
              <span className="rail-title">Versiones</span>
              <ul className="version-list">
                {(detail.data?.versions ?? []).map((version) => (
                  <li key={version.id}><strong translate="no">v{version.version}</strong> <Pill label={version.kind === "published" ? "publicada" : version.kind === "upload" ? "Word" : version.kind === "archived" ? "archivo" : "edición"} tone={version.kind === "published" ? "ok" : "muted"} /> <span className="request-meta">{version.note ?? ""} · {version.author ?? "sistema"} · {formatTimestamp(version.createdAt)}</span></li>
                ))}
              </ul>
            </section>
            <TripDangerZone trip={trip} usage={detail.data?.usage ?? { requests: 0, apartados: 0, saved: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
